import { CSSProperties, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

export interface AutoSizerSize {
  height: number;
  width: number;
}

interface AutoSizerProps {
  children: (size: AutoSizerSize) => ReactNode;
  style?: CSSProperties;
}

const measure = (parent: HTMLElement): AutoSizerSize => {
  const style = window.getComputedStyle(parent);
  const rect = parent.getBoundingClientRect();
  return {
    height: rect.height - parseFloat(style.paddingTop || '0') - parseFloat(style.paddingBottom || '0'),
    width: rect.width - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0')
  };
};

// Sizes its children to its parent's content box. Replaces react-virtualized-auto-sizer, which applied a resize a task after
// ResizeObserver fired, so the browser painted a frame with lists at their old size; when a detail panel opened or collapsed,
// that frame showed a blank gap or cards running under the panel. Applying the size inside the observer callback renders it
// before that paint.
const AutoSizer = ({ children, style }: AutoSizerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<AutoSizerSize>({ height: 0, width: 0 });

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;

    const update = () => {
      const next = measure(parent);
      setSize((prev) => (prev.height === next.height && prev.width === next.width ? prev : next));
    };

    update();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => flushSync(update));
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ overflow: 'visible', height: 0, width: 0, ...style }}>
      {/* Same as the library: nothing to lay out until the parent has been measured. */}
      {size.height > 0 && size.width > 0 && children(size)}
    </div>
  );
};

export default AutoSizer;
